/**
 * ttsPlayer
 *
 * Plays the game's TTS through a dedicated <audio> element using the browser's
 * native MP3 decoder. This replaces hand-rolled PCM scheduling (which produced
 * clicks on Chrome) and waiting-for-the-whole-file (which added 30s+ latency):
 *
 *  - MediaSource Extensions (when supported) append MP3 chunks as they stream
 *    from /api/game/tts, so playback starts within ~1s and decodes gaplessly.
 *  - Buffered fallback (Firefox / no MSE for audio/mpeg) downloads then plays
 *    via a blob URL — still gapless (native decode), just less progressive.
 *
 * speak() resolves when playback actually ENDS, which is the correct moment to
 * start listening (not when bytes finish arriving).
 */

const MSE_MIME = 'audio/mpeg';

function mseSupported(): boolean {
  return (
    typeof MediaSource !== 'undefined' &&
    typeof MediaSource.isTypeSupported === 'function' &&
    MediaSource.isTypeSupported(MSE_MIME)
  );
}

export interface TtsPlayer {
  /** Speak `text`; resolves when playback ends (or is aborted/stopped). */
  speak: (text: string, token: string, signal: AbortSignal) => Promise<void>;
  /** Stop any current playback immediately. */
  stop: () => void;
  /** Prime the <audio> element within a user gesture (helps autoplay policy). */
  unlock: () => void;
  dispose: () => void;
}

export function createTtsPlayer(
  apiBaseUrl: string,
  authHeadersFor: (token: string) => Record<string, string>,
): TtsPlayer {
  const el = new Audio();
  el.preload = 'auto';
  // Not part of any AudioContext graph — plays straight to the output device.
  (el as HTMLAudioElement).crossOrigin = 'anonymous';

  let currentObjectUrl: string | null = null;
  let isPlaying = false;

  const cleanupSrc = () => {
    try {
      el.pause();
    } catch {
      /* ignore */
    }
    el.removeAttribute('src');
    try {
      el.load();
    } catch {
      /* ignore */
    }
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      currentObjectUrl = null;
    }
  };

  const waitForEnd = (signal: AbortSignal) =>
    new Promise<void>((resolve) => {
      const done = () => {
        el.removeEventListener('ended', done);
        el.removeEventListener('error', done);
        signal.removeEventListener('abort', done);
        resolve();
      };
      el.addEventListener('ended', done, { once: true });
      el.addEventListener('error', done, { once: true });
      signal.addEventListener('abort', done, { once: true });
    });

  const speakViaMse = async (text: string, token: string, signal: AbortSignal) => {
    const mediaSource = new MediaSource();
    const objUrl = URL.createObjectURL(mediaSource);
    currentObjectUrl = objUrl;
    el.src = objUrl;

    await new Promise<void>((resolve, reject) => {
      mediaSource.addEventListener('sourceopen', () => resolve(), { once: true });
      mediaSource.addEventListener('error', () => reject(new Error('MediaSource error')), { once: true });
    });
    if (signal.aborted) return;

    const sb = mediaSource.addSourceBuffer(MSE_MIME);
    const queue: Uint8Array[] = [];
    let fetchDone = false;

    const flush = () => {
      if (sb.updating) return;
      if (queue.length > 0) {
        try {
          sb.appendBuffer(queue.shift()!);
        } catch {
          /* transient — updateend will retry */
        }
      } else if (fetchDone && mediaSource.readyState === 'open') {
        try {
          mediaSource.endOfStream();
        } catch {
          /* ignore */
        }
      }
    };
    sb.addEventListener('updateend', flush);

    const resp = await fetch(`${apiBaseUrl}/game/tts`, {
      method: 'POST',
      headers: authHeadersFor(token),
      body: JSON.stringify({ text }),
      signal,
    });
    if (!resp.ok || !resp.body) throw new Error(`tts ${resp.status}`);

    const ended = waitForEnd(signal);
    const reader = resp.body.getReader();
    let started = false;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (signal.aborted) break;
      if (done) {
        fetchDone = true;
        flush();
        break;
      }
      if (value) {
        queue.push(value);
        flush();
        if (!started) {
          started = true;
          isPlaying = true;
          void el.play().catch(() => {});
        }
      }
    }
    await ended;
  };

  const speakBuffered = async (text: string, token: string, signal: AbortSignal) => {
    const resp = await fetch(`${apiBaseUrl}/game/tts`, {
      method: 'POST',
      headers: authHeadersFor(token),
      body: JSON.stringify({ text }),
      signal,
    });
    if (!resp.ok) throw new Error(`tts ${resp.status}`);
    const blob = await resp.blob();
    if (signal.aborted) return;
    const objUrl = URL.createObjectURL(blob);
    currentObjectUrl = objUrl;
    el.src = objUrl;
    const ended = waitForEnd(signal);
    isPlaying = true;
    void el.play().catch(() => {});
    await ended;
  };

  return {
    async speak(text, token, signal) {
      cleanupSrc();
      try {
        if (mseSupported()) {
          await speakViaMse(text, token, signal);
        } else {
          await speakBuffered(text, token, signal);
        }
      } finally {
        isPlaying = false;
        cleanupSrc();
      }
    },
    stop() {
      cleanupSrc();
    },
    unlock() {
      // A muted no-op play inside a user gesture grants the element autoplay
      // permission for later programmatic plays.
      // If the TTS is already actively playing, don't pause it — the element
      // is already unlocked and the user just wants to scroll/interact.
      if (isPlaying) return;
      el.muted = true;
      void el
        .play()
        .then(() => {
          if (!isPlaying) {
            el.pause();
          }
          el.muted = false;
        })
        .catch(() => {
          el.muted = false;
        });
    },
    dispose() {
      cleanupSrc();
    },
  };
}